import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';

import DialogTransition from './DialogTransition';

const PREFIX = 'AgreementDialog';

const classes = {
  content: `${PREFIX}-content`,
  loadingWrapper: `${PREFIX}-loadingWrapper`,
};

const StyledDialog = styled(Dialog)(() => ({
  '& .MuiDialog-paper': {
    // Absolute cap in real CSS px — zooming out can't make this bigger,
    // because zoom doesn't reinterpret px values, only how many fit on screen.
    // NOTE: needs to be updated in case EULA or ESA become much shorter
    height: 'min(1000px, 90vh)',
  },
  [`& .${classes.content}`]: {
    border: 'none',
    width: '100%',
    height: '100%',
  },
  [`& .${classes.loadingWrapper}`]: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

interface AgreementDialogProps {
  open: boolean;
  title: string;
  html?: string;
  isLoading?: boolean;
  acceptLabel: string;
  cancelLabel?: string;
  onAccept: () => void;
  onClose?: () => void;
}

export default function AgreementDialog({
  open,
  title,
  html,
  isLoading = false,
  acceptLabel,
  cancelLabel,
  onAccept,
  onClose,
}: AgreementDialogProps) {
  const [isRead, setIsRead] = React.useState(false);
  const contentRef = React.useRef<HTMLDivElement | null>(null);

  const updateReadState = React.useCallback(() => {
    const elt = contentRef.current;
    if (!elt || isLoading || !html) {
      return;
    }

    const contentFitsWithoutScrolling =
      elt.scrollHeight <= elt.clientHeight + 1;
    const scrolledToBottom =
      elt.scrollTop + elt.clientHeight >= elt.scrollHeight - 50;

    if (contentFitsWithoutScrolling || scrolledToBottom) {
      setIsRead((currentIsRead) => (currentIsRead ? currentIsRead : true));
    }
  }, [html, isLoading]);

  React.useEffect(() => {
    if (open) {
      setIsRead(false);
    }
  }, [html, open]);

  React.useEffect(() => {
    if (!open || isLoading || !html) {
      return undefined;
    }

    const frame = window.requestAnimationFrame(updateReadState);
    window.addEventListener('resize', updateReadState);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', updateReadState);
    };
  }, [html, isLoading, open, updateReadState]);

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      TransitionComponent={DialogTransition}
      scroll="paper"
      disableScrollLock
      maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent ref={contentRef} onScroll={updateReadState}>
        {isLoading ? (
          <div className={classes.loadingWrapper}>
            <CircularProgress />
          </div>
        ) : (
          <div
            className={classes.content}
            dangerouslySetInnerHTML={{
              __html: html || '',
            }}></div>
        )}
      </DialogContent>
      <DialogActions>
        {onClose && cancelLabel && (
          <Button variant="contained" color="primary" onClick={onClose}>
            {cancelLabel}
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          disabled={!isRead}
          onClick={onAccept}>
          {acceptLabel}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
}
